import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X, Loader2 } from 'lucide-react';
import { pollCheckoutStatus } from '../api/client';

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function SubmitSuccessPage() {
  const [search] = useSearchParams();
  const sessionId = search.get('session_id');
  const submissionId = search.get('submission');
  const [status, setStatus] = useState('checking');
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('missing');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await pollCheckoutStatus(sessionId);
        if (cancelled) return;
        setDetail(res);
        if (res.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (res.status === 'expired' || res.status === 'canceled') {
          setStatus('failed');
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="page-container" data-testid="submit-success">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/submit">Submit</Link>
        <span>/</span>
        <span>Payment</span>
      </div>
      <div className="submit-success-wrap">
        {status === 'checking' && (
          <>
            <Loader2 size={40} className="spin" />
            <h1>Confirming your payment…</h1>
            <p className="muted">This usually takes a couple of seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="success-check"><Check size={28} /></div>
            <h1>You are in.</h1>
            <p className="muted">Your listing is being activated. Perks go live within a minute.</p>
            <p className="muted" style={{ fontSize: 12 }}>Submission ID: <code>{submissionId}</code></p>
            <div className="form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
              <Link to="/" className="pill-btn primary lg">Back to the tree</Link>
              <Link to="/submit" className="pill-btn">Submit another</Link>
            </div>
          </>
        )}
        {(status === 'failed' || status === 'error' || status === 'timeout' || status === 'missing') && (
          <>
            <div className="error-check"><X size={28} /></div>
            <h1>Payment {status === 'timeout' ? 'is taking longer than expected' : 'was not completed'}.</h1>
            <p className="muted">
              {status === 'timeout'
                ? "Still waiting on Stripe. Your card was not charged unless you complete the flow. If nothing changes in a few minutes, please email hello@ai-framework.io."
                : 'Your card was not charged. You can retry any time.'}
            </p>
            <div className="form-actions" style={{ justifyContent: 'center', marginTop: 24 }}>
              <Link to="/submit" className="pill-btn primary">Try again</Link>
              <Link to="/" className="pill-btn">Home</Link>
            </div>
          </>
        )}
        {detail && status !== 'checking' && (
          <details style={{ marginTop: 20 }}>
            <summary className="muted">Payment details</summary>
            <pre style={{ background: 'var(--surface)', padding: 12, borderRadius: 8, fontSize: 12, overflowX: 'auto' }}>
              {JSON.stringify(detail, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
